import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { useStore } from 'effector-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Element } from 'react-scroll'
import { getPhoneMaskByCountry } from '~/shared/config/countryConfig'
import {
  FormButtons,
  formButtonsModel,
  FormProvider,
  MaskInput,
  NumericInput,
  Switch,
  TextInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'
import { FormValues, formSchema } from './model'

type FormProps = {
  isCreating?: boolean
  canEdit?: boolean
  isSubmitting?: boolean
  isLoading?: boolean
  onSuccess?: (formValues: FormValues) => void
  defaultValues?: Partial<FormValues>
}

export function Form({
  isCreating,
  canEdit,
  isSubmitting,
  isLoading,
  defaultValues,
  onSuccess,
}: FormProps) {
  const { t } = useTranslation()
  const isEditing = useStore(formButtonsModel.$isEditing)
  const disabled = (!isEditing && !isCreating) || isSubmitting

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  useEffect(() => {
    if (defaultValues) form.reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues, isEditing])

  return (
    <FormProvider form={form} onSuccess={onSuccess} canEdit={canEdit}>
      <Element name='requisites'>
        <Container>
          <ContainerTitle>{t('requisites')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={12}>
              <TextInput
                label='form.designation'
                name='name'
                placeholder='form.enter designation'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={12}>
              <TextInput
                name='fullName'
                label='form.full designation'
                placeholder='form.enter full designation'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={12}>
              <TextInput
                label='form.legal address'
                name='legalAddress'
                placeholder='form.enter legal address'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={12}>
              <TextInput
                label='form.actual address'
                name='actualAddress'
                placeholder='form.enter actual address'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={3}>
              <NumericInput
                label='form.inn'
                name='inn'
                placeholder='form.enter inn'
                skeletonShow={isLoading}
                readOnly={disabled}
                numericProps={{
                  thousandSeparator: '',
                  allowLeadingZeros: true,
                }}
              />
            </Grid>
            <Grid xs={3}>
              <NumericInput
                label='form.kpp'
                name='kpp'
                placeholder='form.enter kpp'
                skeletonShow={isLoading}
                readOnly={disabled}
                numericProps={{
                  thousandSeparator: '',
                  allowLeadingZeros: true,
                }}
              />
            </Grid>
            <Grid xs={3}>
              <NumericInput
                label='form.ogrn'
                name='ogrn'
                placeholder='form.enter ogrn'
                skeletonShow={isLoading}
                readOnly={disabled}
                numericProps={{
                  thousandSeparator: '',
                  allowLeadingZeros: true,
                }}
              />
            </Grid>
            <Grid xs={3}>
              <NumericInput
                label='form.okpo'
                name='okpo'
                placeholder='form.enter okpo'
                skeletonShow={isLoading}
                readOnly={disabled}
                numericProps={{
                  thousandSeparator: '',
                  allowLeadingZeros: true,
                }}
              />
            </Grid>
            <Grid xs={12}>
              <Switch
                labelI18n='form.organization works with vat'
                name='withNds'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
          </Grid>
        </Container>
      </Element>

      <Element name='management'>
        <Container>
          <ContainerTitle>{t('management')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={6}>
              <TextInput
                label='form.position of head'
                name='directorPosition'
                placeholder='form.enter position of head'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={6}>
              <TextInput
                label='form.full name of head'
                name='directorFio'
                placeholder='form.enter full name of head'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={6}>
              <TextInput
                label='form.position of head genitive'
                name='directorPositionInParentalCase'
                placeholder='form.enter position of head genitive'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={6}>
              <TextInput
                label='form.full name of head genitive'
                name='directorFioInParentalCase'
                placeholder='form.enter full name of head genitive'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={6}>
              <TextInput
                label='form.basis for signing documents genitive'
                name='directorBasisForSigningDocuments'
                placeholder='form.enter basis for signing documents genitive'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
          </Grid>
        </Container>
      </Element>

      <Element name='contacts'>
        <Container>
          <ContainerTitle>{t('contacts')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={3}>
              <MaskInput
                name='phoneNumber'
                label='form.phone'
                placeholder='form.enter phone'
                skeletonShow={isLoading}
                readOnly={disabled}
                mask={getPhoneMaskByCountry()}
              />
            </Grid>
            <Grid xs={3}>
              <TextInput
                label='form.E-mail'
                name='email'
                type='email'
                placeholder='form.enter E-mail'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={6}>
              <TextInput
                label='form.website'
                name='webSite'
                placeholder='form.enter website'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
          </Grid>
        </Container>
      </Element>

      <Element name='bank'>
        <Container>
          <ContainerTitle>{t('bank')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={6}>
              <TextInput
                label='form.bank name'
                name='bank'
                placeholder='form.enter bank name'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={6}>
              <NumericInput
                label='form.checking account'
                name='rs'
                placeholder='form.enter checking account'
                skeletonShow={isLoading}
                readOnly={disabled}
                numericProps={{ thousandSeparator: '' }}
              />
            </Grid>
            <Grid xs={6}>
              <NumericInput
                label='form.correspondent account'
                name='ks'
                placeholder='form.enter correspondent account'
                skeletonShow={isLoading}
                readOnly={disabled}
                numericProps={{ thousandSeparator: '' }}
              />
            </Grid>
            <Grid xs={6}>
              <NumericInput
                label='form.bik'
                name='bik'
                placeholder='form.enter bik'
                skeletonShow={isLoading}
                readOnly={disabled}
                numericProps={{ thousandSeparator: '' }}
              />
            </Grid>
          </Grid>
        </Container>
      </Element>
      <FormButtons loading={isSubmitting} isCreating={isCreating} />
    </FormProvider>
  )
}
